import * as THREE from 'three'
import { Vec3 } from '../core/math/vec3'
import { CelestialBody } from '../core/world/body'
import { SYSTEM } from '../core/world/system'
import { OrbitElements, isClosed } from '../core/orbit/elements'
import { campionaConica, Evento } from '../core/orbit/prediction'
import { bodyStateAt, bodyElementsAt } from '../core/orbit/ephemeris'

/**
 * Nella mappa si lavora in chilometri invece che in metri: a dodici milioni
 * di metri i float a 32 bit della GPU avrebbero una precisione di un metro,
 * e le orbite comincerebbero a tremare. In chilometri il problema sparisce.
 */
const SCALA = 1 / 1000
const tre = (v: Vec3): THREE.Vector3 => new THREE.Vector3(v.x * SCALA, v.y * SCALA, v.z * SCALA)

const COLORI = {
  orbita: 0x7fd6a0,
  fuga: 0xffa657,
  manovra: 0x6fa8ff,
  soi: 0x3d5570,
  corpo: 0x8fa3b8,
  evento: 0xffd166,
  impatto: 0xff6b57,
}

function lineaDaPunti(punti: Vec3[], colore: number, opacita = 1): THREE.Line {
  const g = new THREE.BufferGeometry().setFromPoints(punti.map(tre))
  return new THREE.Line(g, new THREE.LineBasicMaterial({
    color: colore, transparent: opacita < 1, opacity: opacita,
  }))
}

function cerchio(raggio: number, colore: number, opacita: number): THREE.Line {
  const punti: THREE.Vector3[] = []
  for (let i = 0; i <= 96; i++) {
    const a = (i / 96) * Math.PI * 2
    punti.push(new THREE.Vector3(Math.cos(a) * raggio * SCALA, Math.sin(a) * raggio * SCALA, 0))
  }
  return new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(punti),
    new THREE.LineDashedMaterial({ color: colore, dashSize: raggio * SCALA * 0.03, gapSize: raggio * SCALA * 0.02, transparent: true, opacity: opacita }),
  )
}

export interface DatiMappa {
  readonly body: CelestialBody
  readonly time: number
  readonly orbita: OrbitElements
  readonly posizione: Vec3
  readonly eventi: Evento[]
  readonly orbitaManovra: OrbitElements | null
  readonly posizioneManovra: Vec3 | null
}

export interface Mappa {
  readonly scene: THREE.Scene
  aggiorna(dati: DatiMappa): void
  inquadra(camera: THREE.PerspectiveCamera, dati: DatiMappa, yaw: number, pitch: number, zoom: number): void
}

export function createMappa(): Mappa {
  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x04070c)
  scene.add(new THREE.AmbientLight(0xffffff, 1))

  const gruppo = new THREE.Group()
  scene.add(gruppo)

  function svuota(): void {
    for (const o of [...gruppo.children]) {
      gruppo.remove(o)
      const m = o as THREE.Line | THREE.Mesh
      m.geometry?.dispose()
      const mat = m.material as THREE.Material | THREE.Material[] | undefined
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
      else mat?.dispose()
    }
  }

  // Object3D.position e di sola lettura: va copiata, non riassegnata.
  const puntino = (p: Vec3, colore: number, raggio: number): THREE.Mesh => {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(raggio, 12, 10),
      new THREE.MeshBasicMaterial({ color: colore }),
    )
    m.position.copy(tre(p))
    return m
  }

  return {
    scene,

    aggiorna(dati) {
      svuota()
      const raggioScena = Math.max(dati.body.radius, 1) * SCALA

      // ── il corpo centrale
      gruppo.add(new THREE.Mesh(
        new THREE.SphereGeometry(raggioScena, 32, 24),
        new THREE.MeshBasicMaterial({ color: 0x2d5a7a }),
      ))
      if (dati.body.atmosphere) {
        gruppo.add(new THREE.Mesh(
          new THREE.SphereGeometry((dati.body.radius + dati.body.atmosphere.ceiling) * SCALA, 24, 18),
          new THREE.MeshBasicMaterial({ color: 0x4a86b8, transparent: true, opacity: 0.18, side: THREE.BackSide }),
        ))
      }

      // ── i figli, con la loro orbita e la loro sfera d'influenza
      for (const figlio of Object.values(SYSTEM)) {
        if (figlio.parentId !== dati.body.id) continue
        const el = bodyElementsAt(figlio, dati.time)
        if (el) gruppo.add(lineaDaPunti(campionaConica(el, 160), COLORI.corpo, 0.4))

        const pos = bodyStateAt(figlio, dati.time).position
        gruppo.add(puntino(pos, COLORI.corpo, Math.max(figlio.radius * SCALA, raggioScena * 0.12)))

        const soi = cerchio(figlio.soiRadius, COLORI.soi, 0.5)
        soi.position.copy(tre(pos))
        soi.computeLineDistances()
        gruppo.add(soi)
      }

      // ── l'orbita del veicolo
      const chiusa = isClosed(dati.orbita)
      gruppo.add(lineaDaPunti(
        campionaConica(dati.orbita, 240, dati.body.soiRadius),
        chiusa ? COLORI.orbita : COLORI.fuga,
      ))

      // ── il veicolo e gli eventi che lo aspettano
      gruppo.add(puntino(dati.posizione, 0xffffff, raggioScena * 0.06))
      for (const e of dati.eventi) {
        const colore = e.tipo === 'impatto' || e.tipo === 'atmosfera' ? COLORI.impatto : COLORI.evento
        gruppo.add(puntino(e.dove, colore, raggioScena * 0.05))
      }

      // ── l'orbita che risulterebbe dalla manovra
      if (dati.orbitaManovra) {
        gruppo.add(lineaDaPunti(
          campionaConica(dati.orbitaManovra, 240, dati.body.soiRadius), COLORI.manovra, 0.9,
        ))
      }
      if (dati.posizioneManovra) {
        gruppo.add(puntino(dati.posizioneManovra, COLORI.manovra, raggioScena * 0.07))
      }
    },

    inquadra(camera, dati, yaw, pitch, zoom) {
      // si inquadra tutto quello che c'e da vedere: l'orbita, o la sfera
      // d'influenza se l'orbita e aperta
      const raggio = isClosed(dati.orbita)
        ? Math.max(dati.orbita.sma * (1 + dati.orbita.ecc), dati.body.radius * 3)
        : Math.min(dati.body.soiRadius, dati.body.radius * 40)
      const d = raggio * SCALA * 2.6 * zoom

      camera.up.set(0, 0, 1)
      camera.position.set(
        Math.cos(yaw) * Math.cos(pitch) * d,
        Math.sin(yaw) * Math.cos(pitch) * d,
        Math.sin(pitch) * d,
      )
      camera.lookAt(0, 0, 0)
      camera.near = Math.max(0.1, d * 1e-4)
      camera.far = d * 20
      camera.updateProjectionMatrix()
    },
  }
}
